import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { MapPin } from "lucide-react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import { SearchableSelect } from "./SearchableSelect";
import { api } from "../lib/api";

/**
 * Venue creation dialog. The country is locked from caller (`countryId`).
 * Resolves states/cities from /api/states and /api/cities.
 *
 * Props:
 *   open, onOpenChange       : Dialog state
 *   countryId                : string (required) — country to scope state/city dropdowns
 *   isAdmin                  : boolean — uses /api/admin/venues if true, else /api/organizer/venues
 *   initialName              : string — prefill for "name"
 *   onCreated(venue)         : called after the venue is created
 */
export const VenueDialog = ({
    open, onOpenChange, countryId, isAdmin,
    initialName = "",
    onCreated,
}) => {
    const { t } = useTranslation();
    const [name, setName] = useState(initialName);
    const [address, setAddress] = useState("");
    const [zipCode, setZipCode] = useState("");
    const [stateId, setStateId] = useState("");
    const [cityId, setCityId] = useState("");
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (open) {
            setName(initialName);
            setAddress("");
            setZipCode("");
            setStateId("");
            setCityId("");
        }
    }, [open, initialName]);

    useEffect(() => {
        if (!countryId || !open) return;
        api.get("/api/states", { params: { country_id: countryId } })
            .then(({ data }) => setStates(data || []));
    }, [countryId, open]);

    useEffect(() => {
        if (!stateId) { setCities([]); return; }
        api.get("/api/cities", { params: { state_id: stateId } })
            .then(({ data }) => setCities(data || []));
        setCityId("");
    }, [stateId]);

    const submit = async (e) => {
        e.preventDefault();
        if (!name.trim() || !address.trim() || !zipCode.trim() || !stateId || !cityId) {
            toast.error(t("venue.fill_required"));
            return;
        }
        setBusy(true);
        try {
            const payload = {
                name: name.trim(),
                address: address.trim(),
                zip_code: zipCode.trim(),
                state_id: stateId,
                city_id: cityId,
            };
            const url = isAdmin ? "/api/admin/venues" : "/api/organizer/venues";
            if (isAdmin) payload.country_id = countryId;
            const { data } = await api.post(url, payload);
            toast.success(t("common.success"));
            onCreated?.(data);
            onOpenChange(false);
        } catch (err) {
            toast.error(err?.response?.data?.detail || t("common.error"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-lg" data-testid="venue-dialog">
                <DialogHeader>
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-muted text-xs font-semibold w-fit mb-2">
                        <MapPin className="h-3.5 w-3.5" />
                        {t("venue.new_label")}
                    </div>
                    <DialogTitle className="font-heading font-black text-2xl">
                        {t("venue.new_title")}
                    </DialogTitle>
                    <DialogDescription className="font-medium">
                        {t("venue.new_subtitle")}
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-4 pt-2">
                    <div>
                        <label className="text-sm font-semibold block mb-1.5">{t("venue.name")} *</label>
                        <input
                            data-testid="venue-name"
                            type="text" required value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="input-modern"
                            placeholder={t("venue.name_placeholder")}
                        />
                    </div>
                    <div>
                        <label className="text-sm font-semibold block mb-1.5">{t("venue.address")} *</label>
                        <input
                            data-testid="venue-address"
                            type="text" required value={address}
                            onChange={(e) => setAddress(e.target.value)}
                            className="input-modern"
                            placeholder={t("venue.address_placeholder")}
                        />
                    </div>
                    <div>
                        <label className="text-sm font-semibold block mb-1.5">{t("venue.zip_code")} *</label>
                        <input
                            data-testid="venue-zip"
                            type="text" required value={zipCode}
                            onChange={(e) => setZipCode(e.target.value)}
                            className="input-modern"
                            placeholder={t("venue.zip_placeholder")}
                        />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <label className="text-sm font-semibold block mb-1.5">{t("auth.state")} *</label>
                            <SearchableSelect
                                testId="venue-state"
                                options={states.map((s) => ({ value: s.id, label: s.name }))}
                                value={stateId}
                                onValueChange={setStateId}
                                placeholder={t("common.choose")}
                                searchPlaceholder={t("common.search")}
                            />
                        </div>
                        <div>
                            <label className="text-sm font-semibold block mb-1.5">{t("auth.city")} *</label>
                            <SearchableSelect
                                testId="venue-city"
                                options={cities.map((c) => ({ value: c.id, label: c.name }))}
                                value={cityId}
                                onValueChange={setCityId}
                                placeholder={t("common.choose")}
                                searchPlaceholder={t("common.search")}
                                disabled={!stateId}
                            />
                        </div>
                    </div>
                    <button
                        type="submit"
                        data-testid="venue-submit"
                        disabled={busy}
                        className="btn-primary w-full disabled:opacity-50"
                    >
                        {busy ? t("common.loading") : t("venue.create")}
                    </button>
                </form>
            </DialogContent>
        </Dialog>
    );
};
